/**
 * Inline (embedded) Connect handler.
 *
 * Renders the hosted Connect UI in an `<iframe>` inside an element the
 * application provides (Plaid `createEmbedded`-style), instead of a popup
 * window. The hosted page does the rest: provider sign-in cannot be framed,
 * so the page itself opens the provider in a top-level window from the
 * user's click and returns the outcome to the frame. This handler therefore
 * never opens a window, never polls for a closed popup, and never navigates
 * the host page — it only owns the iframe and the message channel from it.
 *
 * Messages are accepted only when BOTH hold: `event.origin` is the Alter
 * backend origin AND `event.source` is this handler's own iframe window. The
 * origin check alone is not enough on this surface — a second widget on the
 * same page, or any other Alter-origin window, would otherwise settle this
 * flow.
 */

import type {
  Grant,
  AlterError,
  ConnectCompletion,
  ConnectSessionKind,
} from "../types";
import { DEFAULT_ALTER_HOST } from "../core/config";
import { parseConnectMessage } from "./messages";

/**
 * Attribute stamped on whatever an inline flow mounts in the container (the
 * iframe, or the direct flow's status panel) so integrators and tests can
 * find it. The value names the surface: `"embed"` or `"direct"`.
 */
export const INLINE_MOUNT_ATTR = "data-alter-connect";

/**
 * How long the frame may stay silent after mount before the flow is treated
 * as never having loaded.
 *
 * A cross-origin iframe exposes nothing the SDK can observe: the browser
 * fires `load` even for a frame it refused (a CSP `frame-ancestors` mismatch,
 * a network error page, the backend's JSON 400 for a bad `embed_origin`), so
 * only a message from the hosted page proves it rendered. The hosted page
 * posts `alter_connect_ready` as soon as its script runs, well inside this
 * window even on a slow connection; a page that never posts anything within
 * it did not render. 15 s is generous on purpose: the cost of a false timeout
 * (an error on a page that was merely slow) is higher than the cost of a
 * late one (a blank box the user stares at for a few more seconds).
 */
export const EMBED_LOAD_TIMEOUT_MS = 15_000;

interface EmbedHandlerOptions {
  baseURL: string;
  container: HTMLElement;
  /** The session's kind, from the token; every completion carries it. */
  kind?: ConnectSessionKind;
  onSuccess: (grants: Grant[], completion: ConnectCompletion) => void;
  onError: (error: AlterError) => void;
  onCancel: () => void;
  /** A non-terminal notice from the hosted flow; MUST NOT settle the flow. */
  onNotice?: (eventName: string, metadata: Record<string, unknown>) => void;
  debug?: boolean;
}

export class EmbedHandler {
  private iframe: HTMLIFrameElement | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private loadTimer: number | null = null;
  private settled = false;
  private readonly expectedOrigin: string;
  private readonly options: Required<EmbedHandlerOptions>;

  constructor(options: EmbedHandlerOptions) {
    this.options = {
      baseURL: options.baseURL,
      container: options.container,
      kind: options.kind ?? "oauth",
      onSuccess: options.onSuccess,
      onError: options.onError,
      onCancel: options.onCancel,
      onNotice: options.onNotice || ((): void => {}),
      debug: options.debug || false,
    };
    try {
      this.expectedOrigin = new URL(options.baseURL).origin;
    } catch {
      throw new Error(
        `Invalid baseURL: "${options.baseURL}". Must be a full URL with protocol (e.g., "${DEFAULT_ALTER_HOST}").`,
      );
    }
  }

  /**
   * Mount the hosted UI. Returns the flow mode for `connect_opened`.
   *
   * The listener is registered BEFORE `src` is set: the hosted page may post
   * as soon as it loads, and a message that arrives before the listener
   * exists is lost. The load timer is armed last, after the frame is in the
   * document, and the first message from the frame disarms it.
   */
  start(url: string): "embed" {
    const iframe = document.createElement("iframe");
    // The accessible name a screen reader announces for the frame. Kept
    // neutral: a single-provider session shows no Alter branding anywhere,
    // and that rule covers what assistive technology hears too.
    iframe.title = "Connect account";
    iframe.setAttribute(INLINE_MOUNT_ATTR, "embed");
    // Fill the container; the application sizes the container (350x300 px
    // minimum recommended). No `sandbox`: the hosted page needs its own
    // origin, forms, and the ability to open the provider window.
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "0";
    iframe.style.display = "block";
    this.iframe = iframe;
    this.setupMessageListener();
    iframe.src = url;
    this.options.container.appendChild(iframe);
    this.armLoadTimer();
    this.log("Mounted inline Connect UI");
    return "embed";
  }

  /** Remove the iframe and stop listening. Idempotent. */
  close(): void {
    this.log("Closing inline Connect UI");
    this.clearLoadTimer();
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
      this.messageListener = null;
    }
    if (this.iframe) {
      if (this.iframe.isConnected) {
        this.iframe.remove();
      }
      this.iframe = null;
    }
  }

  private armLoadTimer(): void {
    this.loadTimer = window.setTimeout(() => {
      this.loadTimer = null;
      if (this.settled) return;
      this.log("No message from the frame within the load window");
      this.settled = true;
      this.close();
      this.options.onError({
        code: "embed_load_timeout",
        message:
          "The embedded Connect page did not load. Check that the host page's " +
          "origin equals the origin the session was created with, that the " +
          "host page is the top-level window, and that the Alter host is " +
          "reachable from this browser.",
        details: { timeoutMs: EMBED_LOAD_TIMEOUT_MS },
      });
    }, EMBED_LOAD_TIMEOUT_MS);
  }

  private clearLoadTimer(): void {
    if (this.loadTimer !== null) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }

  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (this.settled) return;
      if (event.origin !== this.expectedOrigin) {
        this.log(
          "Rejected message from unexpected origin:",
          event.origin,
          "(expected:",
          this.expectedOrigin + ")",
        );
        return;
      }
      // Origin alone is not identity: only OUR frame may settle this flow.
      if (!this.iframe || event.source !== this.iframe.contentWindow) {
        this.log("Rejected message from a window that is not this widget");
        return;
      }

      // Any message from our own frame proves the hosted page is running in
      // it, whatever the message says; the load timer's job is done.
      this.clearLoadTimer();

      const outcome = parseConnectMessage(event.data, this.options.kind);
      switch (outcome.kind) {
        case "ignore":
          return;
        case "ready":
          this.log("Inline Connect UI reported ready");
          return;
        case "notice":
          this.log("Connect notice:", outcome.event);
          this.options.onNotice(outcome.event, outcome.metadata);
          return;
        case "exit":
          this.log("User cancelled the inline Connect UI");
          this.settled = true;
          this.close();
          this.options.onCancel();
          return;
        case "success":
          this.log("Connect completion:", outcome.grants.length, "grants");
          this.settled = true;
          this.close();
          this.options.onSuccess(outcome.grants, outcome.completion);
          return;
        case "error":
          this.log("Connect error");
          this.settled = true;
          this.close();
          this.options.onError(outcome.error);
          return;
      }
    };
    window.addEventListener("message", this.messageListener);
  }

  private log(...args: unknown[]): void {
    if (this.options.debug) {
      // Deliberate console.log: this IS the debug logger, opt-in via ``debug``.
      // eslint-disable-next-line no-console
      console.log("[Embed Handler]", ...args);
    }
  }
}
